/**
 * Money + pricing maths. Line items are the single source of truth (spec §4);
 * the pricing block computes everything from their rows — amounts are never
 * hard-coded or read inline from `content`.
 */

import type { Currency, LineItem, VatTreatment } from "@/types";

const UK_VAT_RATE = 0.2;

export function formatMoney(amount: number, currency: Currency = "GBP"): string {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Unknown currency code — fall back to a plain formatted number.
    return `${(currency || "GBP")} ${amount.toFixed(2)}`;
  }
}

export interface PricingTotals {
  /** Required (non-optional) items only. */
  required: LineItem[];
  optional: LineItem[];
  /** Sum of required line_totals — the mandatory base. (The server's
   *  `quote_value` headline is this plus the default-selected optionals.) */
  subtotal: number;
  /** VAT on the payable net (base + selected optional), per vat_treatment. */
  vat: number;
  vatLabel: string;
  /** Payable total: base + selected optional (+ VAT for exclusive). */
  total: number;
  /** Sum of ALL optional line_totals (whether selected or not). */
  optionalTotal: number;
  /** Sum of the SELECTED optional line_totals (live, opt-in). */
  selectedOptionalTotal: number;
}

/**
 * Compute totals from the stored line_total of each row. We trust line_total
 * (it preserves the price as quoted) rather than recomputing quantity*unit_price.
 *
 * `selectedOptionalIds` are the optional items the client has opted into. When
 * omitted (or empty), no optional items count toward the payable total — so the
 * staff editor (no selection) and a fresh public view both show base-only.
 * Optional ticks never change `subtotal` (the mandatory-only headline).
 */
export function computeTotals(
  lineItems: LineItem[],
  // Quotes are VAT-inclusive by default (the staff selector is hidden for now);
  // an explicit stored treatment still wins. See callers' `|| "inclusive"`.
  vatTreatment: VatTreatment = "inclusive",
  selectedOptionalIds?: Iterable<number>,
): PricingTotals {
  const sorted = [...lineItems].sort((a, b) => a.position - b.position);
  const required = sorted.filter((li) => !li.is_optional);
  const optional = sorted.filter((li) => li.is_optional);
  const selected = new Set<number>(selectedOptionalIds ?? []);

  const subtotal = required.reduce((sum, li) => sum + (li.line_total ?? 0), 0);
  const optionalTotal = optional.reduce((sum, li) => sum + (li.line_total ?? 0), 0);
  const selectedOptionalTotal = optional
    .filter((li) => selected.has(li.id))
    .reduce((sum, li) => sum + (li.line_total ?? 0), 0);

  // Payable net = mandatory base + the optional add-ons the client ticked.
  const net = subtotal + selectedOptionalTotal;

  let vat = 0;
  let vatLabel = "VAT not applicable";
  let total = net;

  switch (vatTreatment) {
    case "exclusive":
      vat = net * UK_VAT_RATE;
      vatLabel = "VAT (20%)";
      total = net + vat;
      break;
    case "inclusive":
      // VAT already included in the line totals; show the embedded portion.
      vat = net - net / (1 + UK_VAT_RATE);
      vatLabel = "VAT (20%, included)";
      total = net;
      break;
    case "zero_rated":
      vat = 0;
      vatLabel = "VAT (0%, zero-rated)";
      total = net;
      break;
    case "not_applicable":
    default:
      vat = 0;
      vatLabel = "VAT not applicable";
      total = net;
      break;
  }

  return {
    required,
    optional,
    subtotal,
    vat,
    vatLabel,
    total,
    optionalTotal,
    selectedOptionalTotal,
  };
}

/**
 * The optional rows flagged `default_selected` — the ids to seed into the
 * public/preview selection on load (spec §6). Required rows are always in the
 * base and are excluded. Seed once on mount; user toggles own the state after.
 */
export function defaultSelectedOptionalIds(lineItems: LineItem[]): number[] {
  return lineItems
    .filter((li) => li.is_optional && li.default_selected)
    .map((li) => li.id);
}
